// Main Chat Interface Component for RNAverse AI Chatbot

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Send, Square, Play, ArrowLeft } from 'lucide-react';
import ChatMessage from './ChatMessage';
import SuggestedActions from './SuggestedActions';
import QueryHistorySidebar from './QueryHistorySidebar';
import { sendMessageAsync, sendMessageStream, ChatResponse } from '../services/chatApi';
import TypewriterTagline from './TypewriterTagline';
import { useGuidedDemo } from '../hooks/useGuidedDemo';
import { DEMO_RESPONSES } from '../data/demoResponses';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  agentName?: string;
  data?: any;
  visualization?: any;
  suggestedActions?: string[];
  actions?: any[]; // New structured actions
  timestamp: Date;
  streaming?: boolean; // Track if message is still streaming
}

const ChatInterface: React.FC = () => {
  // ALWAYS start fresh (localStorage disabled for clean UX)
  // Note: We still SAVE to localStorage, but don't LOAD from it on mount
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<Record<string, any>>({});
  const useStreaming = true; // Always use streaming
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamingMsgIdRef = useRef<string | null>(null);

  const userQueries = useMemo(
    () =>
      messages
        .filter((m) => m.role === 'user')
        .map((m) => ({ id: m.id, content: m.content, timestamp: m.timestamp })),
    [messages]
  );

  const scrollToMessage = useCallback((messageId: string) => {
    const el = messageRefs.current[messageId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Persist messages to localStorage
  useEffect(() => {
    try {
      if (messages.length > 0) {
        localStorage.setItem('rnaverse_chat_messages', JSON.stringify(messages));
      }
    } catch (error) {
      console.error('Error saving messages to localStorage:', error);
    }
  }, [messages]);

  // Persist session data to localStorage
  useEffect(() => {
    try {
      if (Object.keys(sessionData).length > 0) {
        localStorage.setItem('rnaverse_session_data', JSON.stringify(sessionData));
      }
    } catch (error) {
      console.error('Error saving sessionData to localStorage:', error);
    }
  }, [sessionData]);

  // Persist conversation ID to localStorage
  useEffect(() => {
    try {
      if (conversationId) {
        localStorage.setItem('rnaverse_conversation_id', conversationId);
      }
    } catch (error) {
      console.error('Error saving conversationId to localStorage:', error);
    }
  }, [conversationId]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Use async processing to bypass 30-second load balancer timeout
      const response: ChatResponse = await sendMessageAsync({
        message: inputValue,
        conversation_id: conversationId,
        session_data: sessionData
      }, (progress, message) => {
        // Progress updates are handled by the streaming handler now
      });

      // DEBUG: Log raw response object
      console.log('=== RAW RESPONSE ===', response);
      console.log('Response is undefined?', response === undefined);
      console.log('Response is null?', response === null);
      console.log('Response type:', typeof response);

      // Check if response is valid
      if (!response) {
        console.error('ERROR: Response is null or undefined!');
        throw new Error('Received empty response from server');
      }

      // Update conversation ID
      if (!conversationId) {
        setConversationId(response.conversation_id);
      }

      // Update session data (critical for context persistence)
      if (response.session_data) {
        console.log('Updating session data:', Object.keys(response.session_data));
        setSessionData(response.session_data);
      }

      // Create assistant message (without showing agent name)
      // Parse timestamp with fallback to current time if invalid
      let timestamp = new Date();
      if (response.timestamp) {
        const parsedTimestamp = new Date(response.timestamp);
        if (!isNaN(parsedTimestamp.getTime())) {
          timestamp = parsedTimestamp;
        }
      }

      // DEBUG: Log response details
      console.log('=== CHAT RESPONSE DEBUG ===');
      console.log('Success:', response.success);
      console.log('Message length:', response.message?.length || 0);
      console.log('Message preview:', response.message?.substring(0, 150));
      console.log('Has data:', !!response.data);
      console.log('Has visualization:', !!response.visualization);
      console.log('Suggested actions:', response.suggested_actions?.length || 0);

      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.message,
        data: response.data,
        visualization: response.visualization,
        suggestedActions: response.suggested_actions,
        timestamp
      };

      console.log('Created message with content length:', assistantMessage.content?.length || 0);

      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Error sending message:', error);

      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: '❌ Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // New streaming handler — accepts optional direct message for auto-submit chips
  const handleSendMessageStream = async (overrideMessage?: string) => {
    const userInputValue = overrideMessage ?? inputValue;
    if (!userInputValue.trim() || isLoading) return;
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: userInputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Create placeholder for assistant response
    const assistantMsgId = `assistant-${Date.now()}`;
    const assistantMessage: Message = {
      id: assistantMsgId,
      role: 'assistant',
      content: 'Thinking...',
      timestamp: new Date(),
      streaming: true
    };

    setMessages(prev => [...prev, assistantMessage]);

    // Create AbortController for stop button
    const controller = new AbortController();
    abortControllerRef.current = controller;
    streamingMsgIdRef.current = assistantMsgId;

    try {
      await sendMessageStream(
        {
          message: userInputValue,
          conversation_id: conversationId,
          session_data: sessionData
        },
        (chunk) => {
          // Handle different chunk types
          if (chunk.text) {
            // Append text to message (clear status messages first)
            setMessages(prev => prev.map(msg => {
              if (msg.id === assistantMsgId) {
                // If content is a status message, replace it; otherwise append
                const isStatusMessage = msg.content === 'Thinking...' || msg.content === 'Running...';
                return {
                  ...msg,
                  content: isStatusMessage ? chunk.text : msg.content + chunk.text
                };
              }
              return msg;
            }));
          } else if (chunk.type === 'visualization') {
            // Add or update visualization (support multiple visualizations per message)
            console.log('🎨 [VISUALIZATION CHUNK RECEIVED]', chunk.visualization);
            console.log('🎨 Visualization type:', chunk.visualization?.type);
            console.log('🎨 Visualization data keys:', Object.keys(chunk.visualization || {}));
            setMessages(prev => prev.map(msg => {
              if (msg.id === assistantMsgId) {
                console.log('🎨 Updating message with visualization:', msg.id);
                // Store visualizations in array to support multiple per message
                const existingVizs = Array.isArray(msg.visualization) ? msg.visualization : (msg.visualization ? [msg.visualization] : []);
                return { ...msg, visualization: [...existingVizs, chunk.visualization] };
              }
              return msg;
            }));
          } else if (chunk.type === 'actions') {
            // Add suggested actions
            setMessages(prev => prev.map(msg =>
              msg.id === assistantMsgId
                ? { ...msg, actions: chunk.actions }
                : msg
            ));
          } else if (chunk.tool_call) {
            // Update message content with simple status — only replace placeholder text,
            // never overwrite real content already streamed before the tool call
            const status = chunk.tool_call.status;
            if (status === 'executing') {
              setMessages(prev => prev.map(msg => {
                if (msg.id !== assistantMsgId) return msg;
                const isPlaceholder = msg.content === 'Thinking...' || msg.content === 'Running...';
                return isPlaceholder ? { ...msg, content: 'Running...' } : msg;
              }));
            }
          } else if (chunk.done) {
            // Mark streaming complete; clear placeholder text if no real content arrived
            setMessages(prev => prev.map(msg => {
              if (msg.id !== assistantMsgId) return msg;
              const isPlaceholder = msg.content === 'Thinking...' || msg.content === 'Running...';
              return { ...msg, streaming: false, content: isPlaceholder ? '' : msg.content };
            }));

            // Update conversation ID
            if (chunk.conversation_id && !conversationId) {
              setConversationId(chunk.conversation_id);
            }

            // Persist session data from backend so next request carries disease/cell type context
            // even if Cloud Run routes to a different instance
            if (chunk.session_data && Object.keys(chunk.session_data).length > 0) {
              setSessionData(chunk.session_data);
            }

            setIsLoading(false);
          } else if (chunk.error) {
            // Handle error
            setMessages(prev => prev.map(msg =>
              msg.id === assistantMsgId
                ? { ...msg, content: `❌ Error: ${chunk.error}`, streaming: false }
                : msg
            ));
            setIsLoading(false);
          }
        },
        controller.signal
      );
    } catch (error: any) {
      // Abort is a graceful stop, not an error
      if (error?.name === 'AbortError') {
        setMessages(prev => prev.map(msg => {
          if (msg.id !== assistantMsgId) return msg;
          const isPlaceholder = msg.content === 'Thinking...' || msg.content === 'Running...';
          return { ...msg, content: isPlaceholder ? 'Stopped.' : msg.content, streaming: false };
        }));
      } else {
        console.error('Streaming error:', error);
        setMessages(prev => prev.map(msg =>
          msg.id === assistantMsgId
            ? { ...msg, content: '❌ Sorry, I encountered an error processing your request. Please try again.', streaming: false }
            : msg
        ));
      }
      setIsLoading(false);
    } finally {
      abortControllerRef.current = null;
      streamingMsgIdRef.current = null;
    }
  };

  const handleStop = () => {
    abortControllerRef.current?.abort();
    if (streamingMsgIdRef.current) {
      const msgId = streamingMsgIdRef.current;
      setMessages(prev => prev.map(msg => {
        if (msg.id !== msgId) return msg;
        // Replace placeholder status with "Stopped"; keep real content if already streamed
        const isPlaceholder = msg.content === 'Thinking...' || msg.content === 'Running...';
        return { ...msg, content: isPlaceholder ? 'Stopped.' : msg.content, streaming: false };
      }));
    }
    setIsLoading(false);
    abortControllerRef.current = null;
    streamingMsgIdRef.current = null;
  };

  const handleSuggestedAction = (action: string) => {
    setInputValue(action);
  };

  const handleVisualizationAction = (action: string, params?: any) => {
    if (action === 'simulate_knockdown' && params?.gene) {
      let query = `Simulate knockdown of ${params.gene}`;
      if (params.disease) query += ` in ${params.disease}`;
      if (params.cell_type) {
        // Avoid "X cell cells" duplication — only append "cells" if type doesn't end with cell/cells
        const ct = params.cell_type.trim();
        const normalized = /cells?$/i.test(ct) ? ct : `${ct} cells`;
        query += ` in ${normalized}`;
      }
      // Patch session so backend uses the correct disease/dataset/cell_type for this appearance
      if (params.disease || params.dataset_id || params.cell_type) {
        setSessionData(prev => ({
          ...prev,
          ...(params.disease && { disease: params.disease }),
          ...(params.dataset_id && { dataset_id: params.dataset_id }),
          ...(params.cell_type && { cell_types: [params.cell_type] }),
        }));
      }
      setInputValue(query);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (useStreaming) {
        handleSendMessageStream();
      } else {
        handleSendMessage();
      }
    }
  };

  // ========================================
  // DEMO MODE
  // ========================================

  const clearChat = useCallback(() => {
    setMessages([]);
    setSessionData({});
    setConversationId(null);
    localStorage.removeItem('rnaverse_chat_messages');
    localStorage.removeItem('rnaverse_session_data');
    localStorage.removeItem('rnaverse_conversation_id');
  }, []);

  const { isDemoRunning, demoStep, highlightMsgId, highlightText, startDemo, exitDemo } = useGuidedDemo({
    setMessages,
    clearChat,
    scrollToMessage,
  });

  const hasMessages = messages.length > 0;

  return (
    <div className="flex h-screen bg-gray-950">
      {/* Query History Sidebar — hidden on mobile */}
      <div className="hidden md:flex h-full">
        <QueryHistorySidebar
          queries={userQueries}
          onQueryClick={scrollToMessage}
        />
      </div>

      {/* Main Chat Column */}
      <div className="flex flex-col flex-1 min-w-0">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800 px-3 md:px-6 py-3">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <img src="/mascot.png?v=5" alt="RNAverse" className="w-8 h-8 md:w-10 md:h-10" />
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-100">
                RNAverse
              </h1>
              <p className="text-xs md:text-sm text-gray-400">
                Genomic Intelligence
              </p>
            </div>
          </div>

          {/* New Chat Button — responsive margin */}
          {hasMessages && (
            <button
              onClick={() => {
                if (isDemoRunning) {
                  exitDemo();
                } else {
                  clearChat();
                }
              }}
              className="mr-4 md:mr-48 flex items-center gap-1.5 text-sm text-gray-300 hover:text-white px-3 md:px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-600 hover:border-gray-500 transition-colors"
              title="Start a new conversation"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
              <span className="hidden sm:inline">New Chat</span>
            </button>
          )}
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto px-2 md:px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {!hasMessages && !isLoading && (
            <div className="flex flex-col items-center justify-center pt-6 md:pt-12 mx-auto max-w-3xl px-4 md:px-8 py-4 md:py-8">
              <div className="flex flex-col items-center w-full">
                {/* Mascot Logo with speech bubble */}
                <div className="relative mb-4 md:mb-6">
                  <img src="/mascot.png?v=5" alt="RNAverse Mascot" className="w-32 h-32 md:w-56 md:h-56 drop-shadow-2xl" />
                  {/* Comic speech bubble with trailing circles */}
                  {/* Small circle closest to bot */}
                  <div className="absolute top-6 left-full ml-1 md:top-10 md:ml-1 w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-[#5e458c80]" />
                  {/* Medium circle */}
                  <div className="absolute top-3 left-full ml-4 md:top-5 md:ml-6 w-3 h-3 md:w-3.5 md:h-3.5 rounded-full bg-[#5e458c80]" />
                  {/* Speech bubble */}
                  <div className="absolute -top-1 left-full ml-8 md:-top-2 md:ml-11 bg-[#5e458c80] rounded-xl px-4 py-2.5 md:px-5 md:py-3 shadow-lg w-[130px] md:w-[155px]">
                    <p className="text-[10px] md:text-xs font-bold text-white leading-relaxed text-center">
                      Hello, I am trained on 100B+ data points.
                    </p>
                  </div>
                </div>

                {/* Typewriter Tagline */}
                <TypewriterTagline />

                {/* Centered Chat Input (landing page only) */}
                <div className="w-full max-w-xl mb-4">
                  <div className="flex gap-2 items-stretch">
                    <div className="flex-1">
                      <textarea
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Ask about diseases, genes, networks, or therapeutic targets..."
                        className="w-full h-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-2xl text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-900/50 focus:border-blue-500 resize-none text-sm"
                        rows={1}
                        disabled={isLoading}
                      />
                    </div>
                    <button
                      onClick={() => handleSendMessageStream()}
                      disabled={isLoading || !inputValue.trim()}
                      className="px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Discover CTA — large button */}
                <button
                  onClick={() => handleSendMessageStream('I am a researcher looking to discover novel lncRNA therapeutic targets. Help me identify the most promising candidates for my disease of interest.')}
                  className="flex items-center gap-3 px-6 py-3 bg-gray-800/60 backdrop-blur-sm border-2 border-indigo-500/60 hover:border-indigo-400 rounded-xl text-left transition-all group hover:bg-gray-800 max-w-sm w-full shadow-lg shadow-indigo-900/20 mb-3"
                >
                  <span className="text-2xl">🚀</span>
                  <div>
                    <span className="text-sm font-semibold text-gray-100 group-hover:text-white">
                      Discover non-coding RNA biology
                    </span>
                    <span className="block text-xs text-gray-500 group-hover:text-gray-400 mt-0.5">
                      AI-guided exploration to find promising lncRNA candidates
                    </span>
                  </div>
                </button>

                {/* Quick Actions — 2x2 grid */}
                <div className="grid grid-cols-2 gap-2 w-full max-w-sm">
                  {[
                    { emoji: '🔬', title: 'MALAT1 Profile', desc: 'Comprehensive lncRNA analysis', query: 'Tell me everything about MALAT1', action: 'input' as const },
                    { emoji: '🎯', title: 'Breast Cancer', desc: 'Top therapeutic targets', query: 'Top targets in breast cancer', action: 'input' as const },
                    { emoji: '⚡', title: 'Knockdown Sim', desc: 'Gene perturbation effects', query: 'Simulate knockdown of LINC01871 in breast cancer in mast cells', action: 'input' as const },
                    { emoji: '▶', title: 'Watch Demo', desc: 'Guided platform tour', query: '', action: 'demo' as const },
                  ].map((chip) => (
                    <button
                      key={chip.title}
                      onClick={() => {
                        if (chip.action === 'demo') {
                          startDemo();
                        } else {
                          handleSendMessageStream(chip.query);
                        }
                      }}
                      className="flex flex-col items-center justify-center gap-1 p-4 bg-gray-800/40 border border-gray-700 rounded-xl text-white hover:border-gray-500 hover:bg-gray-800/60 transition-all text-center"
                    >
                      <span className="text-xl">{chip.emoji}</span>
                      <span className="text-xs font-semibold">{chip.title}</span>
                      <span className="text-[10px] text-gray-500">{chip.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {messages.map((message) => (
            <div
              key={message.id}
              ref={(el) => { messageRefs.current[message.id] = el; }}
            >
              <ChatMessage
                message={message}
                onAction={handleVisualizationAction}
                highlightText={message.id === highlightMsgId ? (highlightText ?? undefined) : undefined}
              />

              {/* Render suggested actions if present */}
              {message.suggestedActions && message.suggestedActions.length > 0 && (
                <div className="mt-4 ml-12">
                  <SuggestedActions
                    actions={message.suggestedActions}
                    onActionClick={handleSuggestedAction}
                  />
                </div>
              )}
            </div>
          ))}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Demo Mode — step progress + Back to Home */}
      {isDemoRunning && (
        <div className="sticky bottom-0 z-50 bg-gray-900/95 backdrop-blur-sm border-t border-gray-700 px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              {DEMO_RESPONSES.map((step, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <div className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    i < demoStep ? 'bg-blue-500'
                    : i === demoStep ? 'bg-purple-500 animate-pulse'
                    : 'bg-gray-700'
                  }`} />
                  <span className={`text-xs hidden sm:inline ${i === demoStep ? 'text-gray-200' : 'text-gray-600'}`}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>
            <button
              onClick={exitDemo}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>
          </div>
        </div>
      )}

      {/* Input Area — shown only when conversation has started, hidden during demo */}
      {!isDemoRunning && hasMessages && <div className="bg-gray-900 border-t border-gray-800 px-2 md:px-4 py-3 md:py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex gap-2 md:gap-3 items-center">
            <div className="flex-1">
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about diseases, genes, networks, or therapeutic targets..."
                className="w-full px-3 md:px-4 py-3 bg-gray-800 border border-gray-700 rounded-2xl text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-900/50 focus:border-blue-500 resize-none text-sm"
                rows={2}
                disabled={isLoading}
              />
            </div>
            {isLoading ? (
              <button
                onClick={handleStop}
                className="p-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all"
                title="Stop generation"
              >
                <Square className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={() => handleSendMessageStream()}
                disabled={!inputValue.trim()}
                className="p-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <Send className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>}
      </div>{/* close Main Chat Column */}
    </div>
  );
};

export default ChatInterface;
